import React, { useContext, useEffect, useRef } from 'react';
import { GridApi, GridOptions } from 'ag-grid-community';
import { useTheme } from '@material-ui/core/styles';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { AgGridTableWrapper } from '@mummssoftware/common-ui';
import { IDGGlobalContext } from '@mummssoftware/idg';

import {
  getColumnDefs,
  commonColumnDefs,
  isQTypeNewIdg,
} from './tableColumnDefs';
import { Context } from '../../context';
import defaults from '../../translations/en.json';
import 'ag-grid-community/dist/styles/ag-grid.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';

if (LicenseManager) {
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY as string);
}

interface VisitLogTableProps {
  viewModePrint?: boolean;
  onSelectChange: (forms: mumms.GenericForm[]) => void;
  /** When the parent clears selection (e.g. after regenerate), AG Grid selection is cleared too. */
  selectedRows?: mumms.GenericForm[];
}

export const DocumentList = ({
  viewModePrint,
  onSelectChange,
  selectedRows,
}: VisitLogTableProps) => {
  const gridApiRef = useRef<GridApi | null>(null);
  const {
    props: { grid: gridTheme },
  } = useTheme() as any;

  const { smallWidget } = useContext(Context);
  const { idgList, documentData } = useContext(IDGGlobalContext);

  useEffect(() => {
    const element: any = document.getElementsByName(
      'idg-document-table-wrapper',
    );
    if (element[1]) {
      element[1].style.display = (documentData && 'none') || 'block';
    }
  }, [documentData]);

  useEffect(() => {
    if (!gridApiRef.current) return;
    const selectedIds = new Set((selectedRows ?? []).map((r) => r.id));
    gridApiRef.current.forEachNode((node) => {
      const shouldBeSelected = selectedIds.has(node.data?.id);
      if (node.isSelected() !== shouldBeSelected) {
        // suppressEvents=true avoids re-triggering onSelectionChanged
        node.setSelected(shouldBeSelected, false, true);
      }
    });
  }, [selectedRows]);

  const gridOptions: GridOptions = {
    masterDetail: !smallWidget,
    embedFullWidthRows: true,
    defaultColDef: {
      ...commonColumnDefs,
    },
    columnDefs: getColumnDefs(smallWidget),
    tooltipShowDelay: 5,
    getRowStyle: (params: any) => {
      if (!isQTypeNewIdg(params?.data)) {
        return { background: 'grey' };
      }
      return undefined;
    },
    onSelectionChanged({ api: gridApi }) {
      const selectedNodes = gridApi.getSelectedNodes();
      selectedNodes.sort(
        (rowA: any, rowB: any) => rowA.rowIndex - rowB.rowIndex,
      );
      const selectedForms = selectedNodes.map((node) => node.data);
      onSelectChange(selectedForms);
    },
    onGridReady({ api }) {
      gridApiRef.current = api;
    },
  };

  const noData = !idgList || !idgList.length || idgList.length === 0;

  return (
    <AgGridTableWrapper
      // id="idg-document-table-wrapper"
      className={`${gridTheme.className} light max-width`}
      printMode={viewModePrint}
      style={{ height: '92%' }}
      name="idg-document-table-wrapper"
    >
      <AgGridReact
        gridOptions={gridOptions}
        rowData={idgList}
        rowHeight={gridTheme.rowHeight}
        headerHeight={gridTheme.headerHeight}
        suppressCellSelection
        suppressScrollOnNewData
        detailRowAutoHeight
        rowSelection="multiple"
        rowMultiSelectWithClick
        overlayNoRowsTemplate={noData ? defaults.noRows : defaults.loading}
      />
    </AgGridTableWrapper>
  );
};

export default DocumentList;
